import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSearch } from '@tanstack/react-router';

// Valid tenant slugs
const VALID_TENANTS = ['matricks', 'silicon', 'ck'] as const;
type TenantSlug = typeof VALID_TENANTS[number];

const DEFAULT_TENANT: TenantSlug = 'matricks';

interface TenantContextType {
  tenant: TenantSlug;
  tenantName: string;
  isDefaultTenant: boolean;
  setTenantFromParam: (param: string | null) => void;
}

const TENANT_NAMES: Record<TenantSlug, string> = {
  matricks: 'Matricks',
  silicon: 'Silicon Labs',
  ck: 'CK Industries',
};

// Subdomain to tenant mapping for multi-domain deployment
const SUBDOMAIN_TENANT_MAP: Record<string, TenantSlug> = {
  'siliconagent': 'silicon',
  'ck': 'ck',
};

/**
 * Detect tenant from subdomain
 * e.g., siliconagent.matricks.ai -> silicon
 */
function detectTenantFromSubdomain(): TenantSlug | null {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;

  // Extract subdomain (first part before the domain)
  // Handles: siliconagent.matricks.ai, dev.matricks.ai, localhost
  const parts = hostname.split('.');

  // Need at least 3 parts for a subdomain (sub.domain.tld)
  if (parts.length >= 3) {
    const subdomain = parts[0];
    if (SUBDOMAIN_TENANT_MAP[subdomain]) {
      return SUBDOMAIN_TENANT_MAP[subdomain];
    }
  }

  return null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

/**
 * Internal component that reads search params from URL
 */
function TenantParamReader({ setTenantFromParam }: { setTenantFromParam: (param: string | null) => void }) {
  // Use TanStack Router's useSearch - returns parsed search params object
  const search = useSearch({ strict: false }) as Record<string, unknown>;

  useEffect(() => {
    const tenantParam = search?.tenant as string | undefined;
    setTenantFromParam(tenantParam || null);
  }, [search, setTenantFromParam]);

  return null;
}

/**
 * TenantProvider - Determines tenant context for the application
 *
 * Tenant detection priority:
 * 1. URL query parameter: /login?tenant=silicon
 * 2. Subdomain detection: siliconagent.matricks.ai -> silicon
 * 3. Default tenant: matricks
 *
 * Post-login, organization comes from JWT token - no tenant context needed.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  // Initialize with subdomain detection or default
  const [tenant, setTenant] = useState<TenantSlug>(() => {
    const subdomainTenant = detectTenantFromSubdomain();
    return subdomainTenant || DEFAULT_TENANT;
  });

  const setTenantFromParam = (param: string | null) => {
    const tenantParam = param?.toLowerCase();
    // URL param takes priority over subdomain
    if (tenantParam && VALID_TENANTS.includes(tenantParam as TenantSlug)) {
      setTenant(tenantParam as TenantSlug);
    }
  };

  const value: TenantContextType = {
    tenant,
    tenantName: TENANT_NAMES[tenant],
    isDefaultTenant: tenant === DEFAULT_TENANT,
    setTenantFromParam,
  };

  return (
    <TenantContext.Provider value={value}>
      <TenantParamReader setTenantFromParam={setTenantFromParam} />
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    // Return default if used outside provider
    return {
      tenant: DEFAULT_TENANT,
      tenantName: TENANT_NAMES[DEFAULT_TENANT],
      isDefaultTenant: true,
      setTenantFromParam: () => {}, // No-op outside provider
    };
  }
  return context;
}

// Export for use in other modules
export { VALID_TENANTS, DEFAULT_TENANT, TENANT_NAMES };
export type { TenantSlug };
